import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { useProject, useDeleteProject } from '../../hooks/useProjects';
import { useCurrentOrganization } from '../../contexts/OrganizationContext';
import { ApiKeyList } from '../../components/projects/ApiKeyList';
import { OAuthProviderList, OAuthBulkImport } from '../../components/oauth';
import { AuthMethodsSettings } from '../../components/settings/AuthMethodsSettings';
import { WebhooksSettings } from '../../components/settings/WebhooksSettings';
import { DomainsSettings } from '../../components/settings/DomainsSettings';
import { AllowedOriginsSettings } from '../../components/settings/AllowedOriginsSettings';
import { RedirectURIsSettings } from '../../components/settings/RedirectURIsSettings';
import { EndUsersSettings } from '../../components/settings/EndUsersSettings';
import { CaptchaSettings } from '../../components/settings/CaptchaSettings';
import { SessionPolicySettings } from '../../components/settings/SessionPolicySettings';
import { ProjectMfaSettings } from '../../components/settings/ProjectMfaSettings';
import { ProjectBrandingSettings } from '../../components/settings/ProjectBrandingSettings';
import { LabsSettings } from '../../components/settings/LabsSettings';
import {
  useProjectCaptchaSettings,
  useUpdateProjectCaptchaSettings,
} from '../../hooks/useCaptchaSettings';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { Button, DescriptionItem, DescriptionList, Tabs, type TabItem } from '../../ui';
import './ProjectDetailPage.css';

type TabValue =
  | 'overview'
  | 'api-keys'
  | 'integrations'
  | 'webhooks'
  | 'domains'
  | 'origins'
  | 'end-users'
  | 'captcha'
  | 'branding'
  | 'settings'
  | 'labs';

const TABS: TabItem<TabValue>[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'api-keys', label: 'API Keys' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'webhooks', label: 'Webhooks' },
  { value: 'domains', label: 'Domains' },
  { value: 'origins', label: 'Origins' },
  { value: 'end-users', label: 'End Users' },
  { value: 'captcha', label: 'CAPTCHA' },
  { value: 'branding', label: 'Branding' },
  { value: 'settings', label: 'Settings' },
  { value: 'labs', label: 'Labs' },
];

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, error } = useProject(projectId);
  const deleteProject = useDeleteProject();
  const { currentRole, currentOrgId, setCurrentOrgId } = useCurrentOrganization();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [activeTab, setActiveTab] = useState<TabValue>('overview');

  useEffect(() => {
    if (project?.organization_id && project.organization_id !== currentOrgId) {
      setCurrentOrgId(project.organization_id);
    }
  }, [project?.organization_id, currentOrgId, setCurrentOrgId]);

  const handleDelete = async () => {
    if (!project) return;

    try {
      await deleteProject.mutateAsync({
        projectId: project.id,
        organizationId: project.organization_id,
      });
      navigate(`/organizations/${project.organization_id}/projects`);
    } catch (err) {
      console.error('Failed to delete project:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="project-detail">
        <div className="project-detail__loading">
          <div className="spinner" />
          <p>Loading project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="project-detail">
        <div className="project-detail__error">
          <p>Failed to load project</p>
          <p className="project-detail__error-detail">{error?.message || 'Project not found'}</p>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail">
      <header className="project-detail__header">
        <nav className="project-detail__breadcrumb" aria-label="Breadcrumb">
          <Link to={`/organizations/${project.organization_id}/projects`}>Projects</Link>
          <span aria-hidden="true">/</span>
          <span>{project.name}</span>
        </nav>
        <div className="project-detail__title-row">
          <div className="project-detail__title">
            <h1>{project.name}</h1>
            <code className="project-detail__slug">{project.slug}</code>
          </div>
          <div className="project-detail__actions">
            <Link
              to={`/projects/${project.id}/edit`}
              className="ui-button ui-button--secondary ui-button--md"
            >
              <span className="ui-button__icon" aria-hidden="true">
                <Pencil size={14} />
              </span>
              Edit
            </Link>
            <Button
              variant="danger"
              leftIcon={<Trash2 size={14} />}
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </Button>
          </div>
        </div>
      </header>

      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        tabs={TABS}
        ariaLabel="Project sections"
        className="project-detail__tabs"
      />

      <div className="project-detail__content">
        {activeTab === 'overview' && (
          <div className="project-detail__overview">
            <section className="project-detail__overview-card">
              <header className="project-detail__overview-header">
                <h3>Project Information</h3>
              </header>
              <DescriptionList>
                <DescriptionItem term="ID">
                  <code>{project.id}</code>
                </DescriptionItem>
                <DescriptionItem term="Name">{project.name}</DescriptionItem>
                <DescriptionItem term="Slug">
                  <code>{project.slug}</code>
                </DescriptionItem>
                {project.description && (
                  <DescriptionItem term="Description">{project.description}</DescriptionItem>
                )}
                <DescriptionItem term="Created">
                  {new Date(project.created_at).toLocaleDateString()}
                </DescriptionItem>
                <DescriptionItem term="Last Updated">
                  {new Date(project.updated_at).toLocaleDateString()}
                </DescriptionItem>
              </DescriptionList>
            </section>
          </div>
        )}

        {activeTab === 'api-keys' && <ApiKeyList projectId={project.id} />}

        {activeTab === 'integrations' && (
          <div className="project-detail__integrations">
            <header className="project-detail__integrations-header">
              <div>
                <h2>OAuth Providers</h2>
                <p className="project-detail__muted">
                  Configure social login providers to allow users to authenticate with external
                  identity providers.
                </p>
              </div>
              <Button variant="secondary" onClick={() => setShowBulkImport(true)}>
                Bulk Import
              </Button>
            </header>
            <OAuthProviderList projectId={project.id} userRole={currentRole ?? undefined} />
          </div>
        )}

        {activeTab === 'webhooks' && (
          <WebhooksSettings projectId={project.id} userRole={currentRole ?? undefined} />
        )}

        {activeTab === 'domains' && (
          <DomainsSettings projectId={project.id} userRole={currentRole ?? undefined} />
        )}

        {activeTab === 'origins' && (
          <div className="project-detail__settings-stack">
            <AllowedOriginsSettings projectId={project.id} userRole={currentRole ?? undefined} />
            <RedirectURIsSettings projectId={project.id} userRole={currentRole ?? undefined} />
          </div>
        )}

        {activeTab === 'end-users' && <EndUsersSettings projectId={project.id} />}

        {activeTab === 'captcha' && (
          <ProjectCaptchaTab
            projectId={project.id}
            canEdit={currentRole === 'admin' || currentRole === 'owner'}
          />
        )}

        {activeTab === 'branding' && (
          <ProjectBrandingSettings
            projectId={project.id}
            canManage={currentRole === 'admin' || currentRole === 'owner'}
          />
        )}

        {activeTab === 'settings' && (
          <div className="project-detail__settings-stack">
            <AuthMethodsSettings
              projectId={project.id}
              userRole={currentRole ?? undefined}
              onConfigureOAuth={() => setActiveTab('integrations')}
            />
            <ProjectMfaSettings
              projectId={project.id}
              canManage={currentRole === 'admin' || currentRole === 'owner'}
            />
            <SessionPolicySettings
              projectId={project.id}
              canManage={currentRole === 'admin' || currentRole === 'owner'}
            />
          </div>
        )}

        {activeTab === 'labs' && <LabsSettings />}
      </div>

      {showDeleteConfirm && (
        <ConfirmDialog
          title="Delete Project"
          message={`Are you sure you want to delete "${project.name}"? This will revoke all API keys and cannot be undone.`}
          confirmLabel="Delete"
          confirmVariant="danger"
          isLoading={deleteProject.isPending}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {showBulkImport && (
        <OAuthBulkImport
          projectId={project.id}
          onClose={() => setShowBulkImport(false)}
          onSuccess={() => setShowBulkImport(false)}
        />
      )}
    </div>
  );
}

function ProjectCaptchaTab({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const { data, isLoading } = useProjectCaptchaSettings(projectId);
  const update = useUpdateProjectCaptchaSettings(projectId);

  return (
    <CaptchaSettings
      scope="project"
      settings={data}
      isLoading={isLoading}
      isSaving={update.isPending}
      canEdit={canEdit}
      onSave={req => update.mutateAsync(req)}
    />
  );
}
